import { useEffect, useRef, useState } from "react";
import { Bike, Loader2, Store } from "lucide-react";
import { Input } from "../../components/ui/input";
import { ItemTexto } from "./Itens";
import { cleoApi } from "./api";

// Só as etapas que o sistema realmente avisa. "Entregue" e "Retirado" nunca
// saíam (o pedido termina como concluído) — achado 363.
const ETAPAS = {
  delivery: [
    ["aguardando_aceite", "Pedido criado"], ["producao", "Em produção"], ["pronto", "Pronto"],
    ["em_rota", "Saiu para entrega"], ["concluido", "Entregue (pedido concluído)"], ["cancelado", "Cancelado"],
  ],
  pickup: [
    ["aguardando_aceite", "Pedido criado"], ["producao", "Em produção"], ["pronto", "Pronto para retirada"],
    ["concluido", "Retirado (pedido concluído)"], ["cancelado", "Cancelado"],
  ],
};
const VARIAVEIS = ["codigo", "endereco", "nome_entregador", "motivo", "link"];
const NOTA_RESUMO = "O texto é o resumo completo do pedido (itens, endereço, pagamento e total), montado pelo sistema. Aqui você liga, desliga e escolhe o atraso.";

/**
 * Avisos de cada etapa do pedido, por tipo de entrega (modelos de
 * /api/order-status-templates). Salva sozinho, cerca de 1 s depois de parar
 * de mexer.
 */
export default function EtapasPedido({ onVerPrevia, onStatus, toast }) {
  const [modelos, setModelos] = useState(null);
  const [tipo, setTipo] = useState("delivery");
  const timers = useRef({});

  useEffect(() => {
    cleoApi.etapas().then((d) => setModelos(d?.templates || [])).catch(() => setModelos([]));
    const pendentes = timers.current;
    return () => Object.values(pendentes).forEach(clearTimeout);
  }, []);

  const achar = (status) => (modelos || []).find((m) => m.tipo_entrega === tipo && m.status === status);

  const mudar = (status, parcial) => {
    setModelos((lista) => lista.map((m) => (m.tipo_entrega === tipo && m.status === status ? { ...m, ...parcial } : m)));
    const chave = `${tipo}:${status}`;
    clearTimeout(timers.current[chave]);
    const tipoAgora = tipo;
    timers.current[chave] = setTimeout(async () => {
      onStatus?.("salvando");
      try {
        const r = await cleoApi.salvarEtapa(tipoAgora, status, parcial);
        if (!r?.success) throw new Error(r?.detail || "Não foi possível salvar.");
        onStatus?.("salvo");
      } catch (e) {
        onStatus?.("");
        toast?.({ title: "Erro ao salvar o aviso", description: e.message, variant: "destructive" });
      }
    }, 900);
  };

  if (modelos === null) {
    return <p className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Carregando os avisos…</p>;
  }

  return (
    <div className="space-y-3">
      <div className="flex gap-1 rounded-xl bg-muted p-1">
        {[["delivery", "Entrega", Bike], ["pickup", "Retirada", Store]].map(([k, rotulo, Icone]) => (
          <button key={k} type="button" onClick={() => setTipo(k)}
            className={`flex-1 inline-flex items-center justify-center gap-2 rounded-lg px-3 py-1.5 text-sm font-semibold transition-colors ${
              tipo === k ? "bg-background shadow-sm" : "text-muted-foreground hover:text-foreground"}`}>
            <Icone className="h-4 w-4" /> {rotulo}
          </button>
        ))}
      </div>
      {ETAPAS[tipo].map(([status, rotulo]) => {
        const m = achar(status);
        if (!m) return null;
        const resumo = status === "aguardando_aceite";
        return (
          <ItemTexto key={`${tipo}:${status}`} label={rotulo}
            ligado={m.is_active === 1 || m.is_active === true} onLigar={(v) => mudar(status, { is_active: v })}
            texto={m.template} onTexto={(t) => mudar(status, { template: t })}
            variaveis={VARIAVEIS} somenteLeitura={resumo} notaLeitura={NOTA_RESUMO}
            onVerPrevia={() => onVerPrevia({
              titulo: rotulo, prefixo: true,
              texto: resumo ? "🛵 *ENTREGA* — Pedido *#12345*\n\n1x Pizza 4 Queijos — R$ 59,90\n\n📍 Rua das Flores, 123\n💳 PIX\n\n*Total: R$ 64,90*\n\n✅ Seu pedido foi recebido e está sendo preparado!" : m.template,
            })}
            extras={(
              <label className="flex items-center gap-2 text-xs text-muted-foreground">
                Enviar depois de
                <Input type="number" min={0} max={3600} value={m.delay_seconds ?? 0}
                  onChange={(e) => mudar(status, { delay_seconds: Math.max(0, parseInt(e.target.value, 10) || 0) })}
                  className="h-7 w-20 text-center text-xs font-semibold tabular-nums" />
                segundos
              </label>
            )} />
        );
      })}
    </div>
  );
}
